import castArray from 'lodash/castArray'
import join from 'lodash/join'
import last from 'lodash/last'
import toNumber from 'lodash/toNumber'
import { GetServerSideProps } from 'next'
import React, { useContext, useEffect } from 'react'

import { SinglePostWrap } from '@Components/CMS/SinglePostWrap'
import { FooterWithGroupVisibility } from '@Components/Footer'
import { HeaderWithGroupVisibility } from '@Components/Header'
import { ProductContext } from '@Components/Product/Context'
import { ProductLinkedData } from '@Components/Product/LinkedData'
import ProductTactics from '@Domains/Product/Features/Tactics/ProductTactics'
import { Page, useCartSummaryQuery } from '@Generated/graphql'
import { initializeApolloFromContext } from '@Globals/Apollo'
import { getProductFieldValue } from '@Globals/CMS/FieldHelpers'
import {
  buildLinkedProductList,
  getAvailableVariantsForProduct,
  getBundledProductList,
  getCrossSellsForProduct,
  getFirstVideoIndexInAssets,
  getGalleryAssetsFromProduct,
  getPersonalizationsFromProduct,
  getTypeAndSubTypeForVariant,
  isGiftWrapping,
} from '@Globals/CMS/ProductHelpers'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { gtm } from '@Globals/Google/DataLayer'
import { gtagEvent } from '@Globals/Google/GTag'
import { MetadataContext } from '@Globals/Metadata'
import { PageHOC } from '@Globals/Page/Client'
import { boostFromContext, handleRedirect, pageProps } from '@Globals/Page/Server'
import { PageProps } from '@Globals/Page/Types'
import { trackProductClick } from '@Globals/Recommendations/ProductClick'
import { OneColumn } from '@Layouts/OneColumn'
import { getSingleProduct } from '@Queries/cms/SingleProduct.query'

interface ProductProps extends PageProps {
  item: Page
}

type Params = {
  slug: string | string[]
}

export const getServerSideProps: GetServerSideProps<ProductProps, Params> = async (context) => {
  const { params } = context
  const slug = castArray(params?.slug || [])

  if (!slug) {
    return {
      notFound: true,
    }
  }

  let productID: string | undefined
  if (slug.length === 1) {
    const productIDNumber = Number(slug[0])
    if (Number.isInteger(productIDNumber) && productIDNumber > 0 && slug[0] === productIDNumber.toString()) {
      // eslint-disable-next-line prefer-destructuring
      productID = slug[0]
    }
  }

  const apolloClient = initializeApolloFromContext(context)
  const post = await getSingleProduct(
    apolloClient,
    `/${join(params?.slug, '')}`,
    productID,
    10,
    undefined,
    undefined,
    boostFromContext(context),
    context?.req?.thSplitTests,
  )

  const { page } = post.data
  if (!page) {
    return {
      notFound: true,
    }
  }

  if (page?.type === 'redirect_permanent' || page?.type === 'redirect_temporary') {
    return handleRedirect(context, page.url, page.type)
  }

  const { id } = page
  if (!id) {
    return {
      notFound: true,
    }
  }

  const product = getProductFieldValue((page as Page)?.fields || [], 'product')
  if (!product) {
    return {
      notFound: true,
    }
  }

  const bundledProducts = getBundledProductList(product)
  const linkedProducts = buildLinkedProductList(product)
  const { availableVariants } = getAvailableVariantsForProduct({ product })
  const unsortedAssets = getGalleryAssetsFromProduct(product)
  const firstVideoIndex = getFirstVideoIndexInAssets(unsortedAssets)
  const crossSells = getCrossSellsForProduct(product)
  const personalisations = getPersonalizationsFromProduct(product)
  const { subType, type } = getTypeAndSubTypeForVariant(product?.masterVariant)

  return {
    props: {
      ...await pageProps(
        context,
        {
          type: 'product',
          id,
          attributes: {
            attribute_sub_type: subType,
            attribute_type: type,
            category_ids: `-${join(product?.categoryIDs, '-')}-`,
            // TODO: clean up unused fields 'guide_info_link_type'
            guide_info_link_type: '',
            // TODO: clean up unused fields 'has_guide_info_link'
            has_guide_info_link: false,
            has_translated_reviews: (product?.productReviews?.translatedCount || 0) > 0,
            has_personalisation: personalisations.length !== 0,
            has_view_more_reviews: (product?.productReviews?.productReviews?.length || 0) < (product?.productReviews?.count || 0),
            // The new design doesn't expand cross sales, so we always set this to false
            has_cross_sale_expandable_list: false,
            is_saleable: product?.availability?.isInStock || false,
            has_cross_sale_products: crossSells.length > 0,
            has_bundled_expandable_list: bundledProducts.length > 3,
            has_bundled_products: bundledProducts.length > 0,
            has_linked_products: linkedProducts.length > 0,
            has_reviews: (product?.productReviews?.productReviews || []).length > 0,
            has_sizes: availableVariants.length > 0,
            is_on_sale: (product?.masterVariant?.price?.discount?.value || 0) > 0,
            has_video: (firstVideoIndex || -1) > -1,
            product_type: product?.type || '',
          },
        },
        page,
        ['cart', 'club', 'common', 'cms', 'footer', 'giftcard', 'header', 'products', 'sidebar', 'shipping', 'validation'],
      ),
      item: page as Page,
    },
  }
}

const Product = PageHOC<ProductProps>(({ item }) => {
  const { store: { config: { currencyCode = '' } = {} } = {} } = useContext(MetadataContext)
  const components = useBlockComponents(item.blocks || [])
  const product = getProductFieldValue(item.fields || [], 'product')

  const { data, loading } = useCartSummaryQuery({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-only',
    skip: !product?.id,
  })

  useEffect(() => {
    if (typeof window === 'undefined' || !product?.id || loading) {
      return
    }

    trackProductClick(product.id, undefined, data?.cart?.customerID || '')
  }, [data, loading, product?.id])

  useEffect(() => {
    if (typeof window !== 'undefined' && product?.id) {
      gtm({
        // TODO: These categoryIds should be consolidated in GTM. But current GTM variable setup is using both.
        categoryContentIds: join(product.categoryIDs, ','),
        categoryIds: join(product.categoryIDs, ','),
        productData: {
          id: product.id,
          // @ts-ignore
          localPrice: toNumber(product.masterVariant?.price?.discount?.plain) || toNumber(product.masterVariant?.price?.plain) || 0,
        },
        productName: product.name || '',
        productId: parseInt(product.id, 10),
      })

      // @ts-ignore
      const discountPlain = toNumber(product.masterVariant?.price?.discount?.plain) || 0
      // @ts-ignore
      const pricePlain = toNumber(product.masterVariant?.price?.plain) || 0

      // value is what the customer pays
      const value = discountPlain || pricePlain || 0
      // price is the normal price without discount
      const price = pricePlain || 0
      // discount is the price difference between value and price
      const discount = price - value

      const lastCategory = last(product.primaryCategories)

      gtagEvent('view_item', {
        currency: currencyCode,
        value,
        items: [
          {
            discount,
            is_gift_wrapping: isGiftWrapping(product),
            item_brand: product.brand?.title || '',
            item_category: product.primaryCategories?.[0]?.name || '',
            item_category2: product.primaryCategories?.[1]?.name || '',
            item_id: product.id,
            item_list_id: lastCategory?.id || '',
            item_list_name: lastCategory?.name || '',
            item_name: product.name || '',
            price,
            quantity: 1,
          },
        ],
      })
    }
  }, [currencyCode, product])

  return (
    <ProductContext.Provider value={product || undefined}>
      <OneColumn footer={<FooterWithGroupVisibility />} header={<HeaderWithGroupVisibility />}>
        <SinglePostWrap>
          {components}
          <ProductTactics group="bronze" />
        </SinglePostWrap>
      </OneColumn>
      <ProductLinkedData />
    </ProductContext.Provider>
  )
})

export default Product
